<script>
  import Img from '../components/Img.svelte';
  import { onMount } from 'svelte';
  import { getOptimizedUrl } from '../lib/cloudinary.js';
  import { buildTheme, parseHex, contrast } from '../lib/theme.js';
  export let title = "We'll be back soon.";
  export let message =
    "Our store is currently undergoing scheduled maintenance. Please check back shortly.";
  export let background = "";
  export let siteName = "Others.";
  export let logo = "";
  export let socials = {};
  export let collectEmails = false;
  export let colors = {}; // the store palette, to know what the page background is

  // The logo is rendered as a flat single colour — black or white, whichever contrasts with what is
  // actually behind it: a photo background is dimmed dark by the overlay (→ white); otherwise it's the
  // store's page colour, and we pick whichever of black/white has the higher contrast ratio on it.
  $: pageBg = parseHex(buildTheme(colors || {}).hex.background) || [255, 255, 255];
  $: logoWhite = background ? true : contrast([255, 255, 255], pageBg) > contrast([0, 0, 0], pageBg);
  $: logoFilter = logoWhite ? 'brightness(0) invert(1)' : 'brightness(0)';

  // A flat recolour only makes sense for a logo with a transparent background — on an opaque JPG it
  // would turn into a solid block. Check the corners; if the logo is opaque (or can't be read), leave it as is...
  // except an unreadable one, which is assumed transparent (the common case).
  let logoHasAlpha = true;
  onMount(() => {
    if (!logo) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = c.height = 40;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, 40, 40);
        const alphaAt = (x, y) => ctx.getImageData(x, y, 1, 1).data[3];
        logoHasAlpha = [alphaAt(0, 0), alphaAt(39, 0), alphaAt(0, 39), alphaAt(39, 39)].some(a => a < 250);
      } catch { logoHasAlpha = true; }
    };
    img.src = getOptimizedUrl(logo, 160);
  });

  let email = "";
  // What happened to the address: 'done' (subscribed), 'pending' (we emailed a confirmation link), 'already' (it was on the list), or '' (not submitted yet).
  let outcome = "";
  let submitting = false;
  let submitError = "";

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || submitting) return;
    submitting = true;
    submitError = "";
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 429) throw new Error("Too many attempts from this connection. Please try again later.");
      if (!res.ok) throw new Error(body.error || "Something went wrong. Please try again.");
      outcome = body.already ? "already" : body.pending ? "pending" : "done";
    } catch (err) {
      submitError = err instanceof TypeError ? "Couldn't reach the server. Check your connection and try again." : err.message;
    } finally {
      submitting = false;
    }
  }
</script>

<svelte:head>
  <title>Maintenance — {siteName}</title>
  <meta name="robots" content="noindex" />
</svelte:head>

<div
  class="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-background"
  style={background
    ? `background-image: url('${getOptimizedUrl(background, 1920)}'); background-size: cover; background-position: center;`
    : ""}
>
  {#if background}
    <div class="absolute inset-0 bg-black/60 backdrop-blur-[2px]"></div>
  {/if}

  <div
    class="relative z-10 text-center max-w-lg px-8 py-16 {background
      ? 'text-white'
      : ''}"
  >
    {#if logo}
      <Img src={logo} alt={siteName} widths={[160, 320, 480]} fallbackWidth={320} sizes="200px" priority style={logoHasAlpha ? `filter: ${logoFilter}` : ''} class="h-10 w-auto mx-auto mb-10 object-contain {logoHasAlpha && logoWhite ? 'drop-shadow-sm' : ''}" />
    {:else}
      <p class="text-label tracking-[0.4em] mb-10 opacity-60">{siteName}</p>
    {/if}

    <h1 class="font-display text-3xl md:text-4xl font-bold mb-5 leading-tight md:leading-[1.1111]">
      {title}
    </h1>
    <p class="text-sm leading-relaxed opacity-75 max-w-sm mx-auto">{message}</p>

    {#if collectEmails}
      <div class="mt-10">
        {#if outcome}
          <div
            class="flex items-center justify-center gap-2 text-sm {background
              ? 'text-white'
              : 'text-foreground'} opacity-80"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"><path d="M20 6 9 17l-5-5" /></svg
            >
            <span role="status">
              {#if outcome === "already"}
                You're already on the list — we'll let you know when we're back.
              {:else if outcome === "pending"}
                Almost there — check your inbox and confirm your email to get notified.
              {:else}
                You're on the list — we'll notify you when we're back!
              {/if}
            </span>
          </div>
        {:else}
          <p class="text-xs opacity-60 mb-3 tracking-widest uppercase">
            Notify me when you're back
          </p>
          <form onsubmit={handleSubmit} class="flex gap-2 max-w-sm mx-auto">
            <input
              type="email"
              required
              bind:value={email}
              placeholder="your@email.com"
              class="flex-1 px-4 py-2.5 text-sm bg-transparent border focus:outline-hidden transition-colors
                {background
                ? 'border-white/40 text-white placeholder-white/40 focus:border-white/80'
                : 'border-border focus:border-foreground'}"
            />
            <button
              type="submit"
              disabled={submitting}
              aria-label="Notify me"
              class="px-5 py-2.5 text-[10px] tracking-[0.2em] uppercase font-medium transition-colors disabled:opacity-60
                {background
                ? 'bg-white text-black hover:bg-white/90'
                : 'bg-foreground text-primary-foreground hover:bg-foreground/90'}"
            >
              {submitting ? "…" : "NOTIFY"}
            </button>
          </form>
          {#if submitError}
            <p class="text-xs text-red-400 mt-2">{submitError}</p>
          {/if}
        {/if}
      </div>
    {/if}

    <!-- Social Links -->
    {#if socials && (socials.instagram?.trim() || socials.twitter?.trim() || socials.tiktok?.trim() || socials.youtube?.trim())}
      <div class="flex items-center justify-center gap-6 mt-10">
        {#if socials.instagram?.trim()}
          <a
            href={socials.instagram.trim()}
            target="_blank"
            rel="noopener noreferrer"
            class="opacity-60 hover:opacity-100 transition-opacity"
            aria-label="Instagram"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              ><rect x="2" y="2" width="20" height="20" rx="5" ry="5"
              ></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"
              ></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg
            >
          </a>
        {/if}
        {#if socials.twitter?.trim()}
          <a
            href={socials.twitter.trim()}
            target="_blank"
            rel="noopener noreferrer"
            class="opacity-60 hover:opacity-100 transition-opacity"
            aria-label="X (Twitter)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              ><path
                d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
              /></svg
            >
          </a>
        {/if}
        {#if socials.tiktok?.trim()}
          <a
            href={socials.tiktok.trim()}
            target="_blank"
            rel="noopener noreferrer"
            class="opacity-60 hover:opacity-100 transition-opacity"
            aria-label="TikTok"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              ><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"></path></svg
            >
          </a>
        {/if}
        {#if socials.youtube?.trim()}
          <a
            href={socials.youtube.trim()}
            target="_blank"
            rel="noopener noreferrer"
            class="opacity-60 hover:opacity-100 transition-opacity"
            aria-label="YouTube"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="currentColor"
              ><path
                d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
              /></svg
            >
          </a>
        {/if}
      </div>
    {/if}
  </div>
</div>
